import { Platform } from 'react-native';

import ENV from 'react-native-config';

import { addDays, endOfDay, millisecondsToSeconds } from 'date-fns';
import qs from 'query-string';

import { getCatchupProgramsPeriodFilter } from 'store/details/utils/constants';
import { RBDeviceType, RBProgram } from 'store/railsBuilder/reducer.types';
import {
    DeleteRecordingOptions,
    RecordingActionType,
    RecordingsQueryParams,
} from 'store/recordings/actions.types';
import { PlaybackPlatforms } from 'store/recordings/reducer.types';
import { Profile } from 'store/user/reducer.types';

import { isSafari } from 'utils/responsiveness';

import { SearchType } from 'screens/Search/types';

const { LICENSE_SERVER, TENANT_ID } = ENV;

export const DEVICE_TYPE = Platform.select<RBDeviceType>({
    ios: 'IOS',
    android: 'Android',
    web: isSafari ? 'IOS' : 'Browser',
    default: 'IOS',
});
export const NPVR_PLAYBACK_DEVICE_TYPE = Platform.select<
    keyof PlaybackPlatforms | undefined
>({
    ios: 'IOS',
    android: 'Android',
    web: isSafari ? 'IOS' : 'Browser',
    default: undefined,
});

const iosLicenseUrl = `${LICENSE_SERVER}/${TENANT_ID}/fpls/contentlicenseservice/v1/licenses`;
const androidLicenseUrl = `${LICENSE_SERVER}/${TENANT_ID}/wvls/contentlicenseservice/v1/licenses`;

const iosSSM = `${LICENSE_SERVER}/${TENANT_ID}/ssm/v1`;
const androidSSM = `${LICENSE_SERVER}/${TENANT_ID}/ssm/v1`;

const epgProgramFields = `[${[
    'editorial.technicals',
    'editorial.id',
    'editorial.title',
    'editorial.Ratings',
    'editorial.Description',
    'editorial.seriesRef',
    'editorial.SeasonNumber',
    'editorial.episodeNumber',
    'editorial.Images',
    'editorial.PromoImages',
    'editorial.CUStartDate',
    'editorial.CUEndDate',
    'editorial.type',
    'editorial.Countries',
    'editorial.contentType',
    'id',
    'seriesRef',
    'contentRef',
    'serviceRef',
    'isStartOver',
    'isCatchUp',
    'period',
    'title',
    'Title',
    'SeriesTitle',
    'CUStartDate',
    'CUEndDate',
    'Description',
    'Rating',
    'Ratings',
    'links',
    'type',
    'isSTCU',
    'isLTCU',
    'isnPvr',
    'nPvrSupport',
    'SeasonNumber',
    'EpisodeNumber',
    'episodeNumber',
    'Episode',
    'Countries',
    'airingStartTime',
    'airingEndTime',
    'clientLiveTrickModeControl',
    'clientVODTrickModeControl',
    'clientRecordingTrickModeControl',
    'clientTimeshiftControl',
    'clientGlobalBlackoutControl',
    'clientMobileOOHCatchupControl',
    'clientMobileLiveOOHStreamingControl',
    'clientMobileRestartOOHControl',
    'clientMobileOOHNpvrControl',
    ...Platform.select({
        web: ['clientWebBlackoutControl', 'clientWebRestartControl'],
        default: ['clientMobileBlackoutControl', 'clientMobileRestartControl'],
    }),
].reduce((acc, param, index, array) => {
    return `${acc}"${param}"${array.length - 1 === index ? '' : ','}`;
}, '')}]`;

const programDetailsFields = JSON.stringify([
    'id',
    'contentRef',
    'seriesRef',
    'serviceRef',
    'isnPvr',
    'nPvrSupport',
    'isStartOver',
    'Subtitles',
    'Audio',
    'SeasonNumber',
    'Ratings',
    'SeriesTitle',
    'Title',
    'title',
    'episodeNumber',
    'EpisodeTitle',
    'Episode',
    'Genre',
    'Countries',
    'Description',
    'Year',
    'airingStartTime',
    'airingEndTime',
    'clientLiveTrickModeControl',
    'clientVODTrickModeControl',
    'clientRecordingTrickModeControl',
    'clientTimeshiftControl',
    'clientGlobalBlackoutControl',
    'clientMobileOOHCatchupControl',
    'clientMobileLiveOOHStreamingControl',
    'clientMobileRestartOOHControl',
    'clientMobileOOHNpvrControl',
    'period.start',
    'period.end',
    'period.duration',
    'editorial.contentType',
    'editorial.technicals.ProgrammeStartDate',
    'editorial.technicals.ProgrammeEndDate',
    'editorial.technicals.deviceType',
    'editorial.technicals.id',
    'editorial.technicals.isLTCU',
    'editorial.technicals.isSTCU',
    'editorial.technicals.CUStartDate',
    'editorial.technicals.CUEndDate',
    'editorial.technicals.period.start',
    'editorial.technicals.period.end',
    'editorial.technicals.duration',
    'editorial.technicals.media.AV_PlaylistName',
    ...Platform.select({
        web: [
            'clientWebBlackoutControl',
            'clientWebRestartControl',
            'clientWebNpvrControl',
        ],
        default: [
            'clientMobileBlackoutControl',
            'clientMobileRestartControl',
            'clientMobileNpvrControl',
        ],
    }),
]);

const vodDetailsFields = JSON.stringify([
    'editorial.id',
    'editorial.seriesRef',
    'editorial.serviceRef',
    'editorial.isnPvr',
    'editorial.Subtitles',
    'editorial.Audio',
    'editorial.SeasonNumber',
    'editorial.Ratings',
    'editorial.SeriesTitle',
    'editorial.Title',
    'editorial.title',
    'editorial.episodeNumber',
    'editorial.EpisodeTitle',
    'editorial.Episode',
    'editorial.Genre',
    'editorial.Countries',
    'editorial.Description',
    'editorial.Year',
    'editorial.NetDuration',
    'editorial.airingStartTime',
    'editorial.airingEndTime',
    'editorial.clientLiveTrickModeControl',
    'editorial.clientVODTrickModeControl',
    'editorial.clientRecordingTrickModeControl',
    'editorial.clientTimeshiftControl',
    'editorial.clientGlobalBlackoutControl',
    'editorial.clientMobileOOHCatchupControl',
    'editorial.clientMobileLiveOOHStreamingControl',
    'editorial.clientMobileRestartOOHControl',
    'editorial.clientMobileOOHNpvrControl',
    'editorial.clientMobileVODOOHControl',
    'editorial.period.start',
    'editorial.period.end',
    'editorial.period.duration',
    'editorial.contentType',
    'technical.ProgrammeStartDate',
    'technical.ServiceId',
    'technical.ProgrammeEndDate',
    'technical.deviceType',
    'technical.id',
    'technical.isLTCU',
    'technical.isSTCU',
    'technical.CUStartDate',
    'technical.CUEndDate',
    'technical.period.start',
    'technical.period.end',
    'technical.duration',
    'technical.media.AV_PlaylistName',
    'product.id',
    'product.type',
    'product.price',
    'product.rentalDuration',
    ...Platform.select({
        web: [
            'editorial.clientWebBlackoutControl',
            'editorial.clientWebRestartControl',
        ],
        default: [
            'editorial.clientMobileBlackoutControl',
            'editorial.clientMobileRestartControl',
        ],
    }),
]);

export const RECORDING_MAIN_METADATA_LANGUAGE = 'hu_HU';
export const RECORDING_LOCALIZED_FIELDS = [
    'title',
    'Title',
    'SeriesTitle',
    'episode',
    'Genre',
    'Audio',
    'Subtitles',
    'Description',
    'Ratings',
];
const DEFAULT_CDVR_RECORDINGS_FIELDS = [
    'id',
    'seriesId',
    'seriesRecordingId',
    'status',
    'captureEnd',
    'captureDuration',
    'contentId',
    'isProtected',
    'channelId',
    'eventId',
    'contentType',
    `playback.${NPVR_PLAYBACK_DEVICE_TYPE}`,

    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientGlobalBlackoutControl`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientRecordingTrickModeControl`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientTimeshiftControl`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileOOHNpvrControl`,
    ...Platform.select({
        ios: [
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileBlackoutControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileRestartControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileNpvrControl`,
        ],
        android: [
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileBlackoutControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileRestartControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientMobileNpvrControl`,
        ],
        web: [
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientWebBlackoutControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientWebRestartControl`,
            `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.clientWebNpvrControl`,
        ],
        default: [],
    }),

    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.id`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.serviceRef`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.editorial.id`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.editorial.seriesRef`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.editorial.technicals`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.period.start`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.period.end`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.period.duration`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.episodeNumber`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.SeasonNumber`,
    `metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.isnPvr`,

    ...[RECORDING_MAIN_METADATA_LANGUAGE, 'en_GB'].reduce<string[]>(
        (acc, language) => [
            ...acc,
            ...RECORDING_LOCALIZED_FIELDS.map(
                (field) => `metadata.${language}.${field}`,
            ),
        ],
        [],
    ),
];

// Nagra endpoints
export const getSsoSignInUrl = () => '/ags/signOn';
export const getSessionTolenSignInUrl = () => '/ags/signOnWithSessionToken';
export const getLogout = () => 'ias/v3/token/actions/signout';
export const getRefreshToken = () => `/ias/v3/token/actions/refresh`;

export const getRbTemplateviewsHierarchy = () =>
    '/contentdelivery/v1/templateviews/hierarchy';

export const getRBTemplateviewSummary = (templateId: string) =>
    qs.stringifyUrl({
        url: `/contentdelivery/v1/templatesummary/${templateId}`,
    });

export const getRbTemplateviewContentByRail = (
    templateId: string,
    railId: string,
    queryParams: { limit?: number; page?: number },
) =>
    qs.stringifyUrl({
        url: `/contentdelivery/v2/templateviews/${templateId}/rails/${railId}`,
        query: queryParams,
    });

export const getChannelsUrl = () =>
    `/metadata/delivery/GLOBAL/btv/services?sort=[["editorial.tvChannel",1]]&filter={"technical.deviceType":{"$in":["${DEVICE_TYPE}"]}}&limit=10000&all_ages=true`;

export const getVodDetailsUrl = (query: {
    filter: string;
    limit?: number;
    page?: number;
    distinct?: string;
    fields?: string;
    sort?: string;
}) =>
    qs.stringifyUrl({
        url: 'metadata/delivery/v2/GLOBAL/vod/editorials',
        query: { fields: vodDetailsFields, ...query },
    });

export const getProgramDetailsUrl = (query: {
    filter: string;
    limit?: number;
    page?: number;
    distinct?: string;
    fields?: string;
    sort?: string;
}) =>
    qs.stringifyUrl({
        url: '/metadata/delivery/GLOBAL/btv/programmes',
        query: { fields: programDetailsFields, ...query },
    });

export const getProgramById = (id: RBProgram['id']) =>
    `/metadata/delivery/GLOBAL/btv/programmes?filter={"id": "${id}"}&limit=2`;

export const getEditorialByContentRefAndProgramId = (
    contentRef: string,
    programId: string,
) =>
    `metadata/delivery/v2/GLOBAL/vod/editorials?filter={"editorial.id":"${contentRef}","technical.ProgramId":"${programId}"}`;

export const getRentAssetUrl = () =>
    `/purchase_service/v1/user/product/actions/purchase`;

export const getEpgUrl = (queryParams: {
    from?: number | string;
    to?: number | string;
    channel?: string;
    limit?: number;
    page?: number;
    deviceType?: RBDeviceType;
}) =>
    qs.stringifyUrl({
        url: `/metadata/v1/epg`,
        query: {
            ...queryParams,
            fields: epgProgramFields,
        },
    });

export const getEpgNowNext = (queryParams: {
    channel: string;
    next?: boolean;
    previous?: boolean;
    deviceType?: RBDeviceType;
}) =>
    qs.stringifyUrl({
        url: `/metadata/v1/epg/now`,
        query: {
            ...queryParams,
            fields: epgProgramFields,
        },
    });

export const getContentTokenUrl = (content_id: string) =>
    `/ias/v2/content_token?content_id=${content_id}&type=device`;

export const getDrmLicensesUrl = () =>
    Platform.select({
        ios: iosLicenseUrl,
        android: androidLicenseUrl,
        web: isSafari ? iosLicenseUrl : androidLicenseUrl,
        default: androidLicenseUrl,
    });
export const getDrmCertificatesUrl = () =>
    `${LICENSE_SERVER}/${TENANT_ID}/fpls/contentlicenseservice/v1/certificates`;
export const getSsmServerURL = () =>
    Platform.select({
        ios: iosSSM,
        android: androidSSM,
        web: isSafari ? iosSSM : androidSSM,
    });

export const getEntitlementsUrl = () => `/rmg/v1/user/entitlements`;

export const getAccountInfo = () => `/adm/v1/user/accounts`;

export const getPinsStatusUrl = () => `/adm/v1/user/accounts/pins/status`;

export const getCreatePinUrl = () => `/adm/v1/user/accounts/actions/pin`;

export const getValidatePinUrl = () =>
    `/adm/v1/user/accounts/actions/validatePin`;

export const getChangePinUrl = () => `/adm/v1/user/accounts/actions/changePin`;

export const getUpdateRatingsThresholdsUrl = () =>
    `/adm/v1/user/accounts/parentalControl/actions/updateRatingsThresholds`;

export const getProfilesUrl = () => '/ups/v1/user/userprofiles';

export const getProfileUrl = (id: Profile['_id']) =>
    `/ups/v1/user/userprofiles/${id}`;

export const changeUserProfileUrl = () =>
    '/ias/v3/token/actions/changeUserProfile';

export const addProfileUrl = () => '/ups/v1/user/userprofiles';

export const editProfileUrl = (id: string) => `/ups/v1/user/userprofiles/${id}`;

export const getSearchFacadeUrl = (queryParams: {
    limit?: number;
    page?: number;
    offset?: number;
    type: SearchType;
    device: typeof DEVICE_TYPE;
    q: string;
}) =>
    qs.stringifyUrl({
        url: `/search/v1/search`,
        query: queryParams,
    });

export const getCastCrewPrograms = (contentRef: string, access_token: string) =>
    `persons/v1/client/castCrewPersonas?filter={"playlist.id": "${contentRef}"}&token=${access_token}`;

export const getRegisterDeviceMessagingServiceUrl = (deviceId: string) =>
    `/odm/v1/opendevice/${deviceId}/queue`;

export const getFavouritesListsUrl = (accountId: string) =>
    `/useractivityvault/v1/clientdata/account/${accountId}/favouriteslists`;

export const updateFavouritesListsUrl = (accountId: string, listId: string) =>
    `/useractivityvault/v1/clientdata/account/${accountId}/favouriteslists/${listId}`;

export const getBookmarksUrl = (accountId: string) =>
    `/useractivityvault/v1/clientdata/account/${accountId}/bookmarks`;

export const setBookmarkUrl = (
    accountId: string,
    contentId: string,
    expires?: number,
) =>
    qs.stringifyUrl({
        url: `/useractivityvault/v1/clientdata/account/${accountId}/bookmarks/${contentId}`,
        query: { expires },
    });

export const getRecordingSeasonsUrl = (seriesId: string) =>
    `/cdvr/v2/recordings/browse/series/${seriesId}`;

export const getRecordingSeasonEpisodesUrl = (
    seasonId: string | null,
    queryParams?: {
        page?: number;
        limit?: number;
    },
) => {
    return qs.stringifyUrl({
        url: `/cdvr/v2/recordings/seasons/${seasonId}`,
        query: {
            fields: JSON.stringify(DEFAULT_CDVR_RECORDINGS_FIELDS),
            filter: JSON.stringify({
                deviceType: NPVR_PLAYBACK_DEVICE_TYPE,
            }),
            sort: 'METADATA_ASC',
            sortField: 'EPISODE_NUMBER',
            ...queryParams,
        },
    });
};

export const getRecordingsUrl = (
    queryParams: RecordingsQueryParams,
): string => {
    const { fields = DEFAULT_CDVR_RECORDINGS_FIELDS, filter } = queryParams;

    return qs.stringifyUrl({
        url: `/cdvr/v2/recordings`,
        query: {
            ...queryParams,
            fields: fields && JSON.stringify(fields),
            filter:
                filter &&
                JSON.stringify({
                    ...filter,
                    deviceType: NPVR_PLAYBACK_DEVICE_TYPE,
                }),
        },
    });
};

export const getSeriesRecordingsUrl = () =>
    qs.stringifyUrl({
        url: '/cdvr/v1/seriesrecordings/status',
        query: { limit: 1000000 },
    });

export const getRecordingsQuotaUsageUrl = () => `/cdvr/v1/account/quotausage`;

export const scheduleRecordingUrl = () => `/cdvr/v1/recordings`;
export const scheduleSeriesRecordingUrl = () => `/cdvr/v1/seriesrecordings`;

export const protectUnprotectRecordingUrl = (recordingId: string) =>
    `/cdvr/v1/recordings/${recordingId}`;

export const deleteRecordingUrl = (
    recordingId: string,
    action: RecordingActionType,
) => `/cdvr/v1/recordings/${recordingId}?action=${action}`;

export const deleteSeriesRecordingUrl = (
    seriesRecordingId: string,
    deleteRecordingOption: DeleteRecordingOptions,
    action: RecordingActionType,
) =>
    `/cdvr/v1/seriesrecordings/${seriesRecordingId}?deleteRecordingOption=${deleteRecordingOption}&action=${action}`;

export const getNextCatchupEpisodeUrl = (
    seriesRef: string,
    seasonNumber: number,
    episodeNumber: number,
) => {
    const query = {
        filter: JSON.stringify({
            'editorial.seriesRef': { $in: [seriesRef] },
            ...getCatchupProgramsPeriodFilter(),
            $or: [
                {
                    'editorial.SeasonNumber': seasonNumber,
                    'editorial.episodeNumber': { $gt: episodeNumber },
                },
                { 'editorial.SeasonNumber': { $gt: seasonNumber } },
            ],
        }),
        limit: 1,
        sort: JSON.stringify([
            ['editorial.SeasonNumber', 1],
            ['editorial.episodeNumber', 1],
        ]),
    };

    return getProgramDetailsUrl(query);
};

export const getNextNpvrEpisodeUrl = (
    seriesId: string,
    channelId: string,
    seasonNumber: number,
    episodeNumber: number,
) => {
    const queryParams = {
        filter: {
            seriesId,
            channelId,
            status: { $in: ['RECORDED', 'RECORDING'] },
            $or: [
                {
                    [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.SeasonNumber`]:
                        seasonNumber,
                    [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.episodeNumber`]:
                        { $gt: episodeNumber },
                },
                {
                    [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.SeasonNumber`]:
                        { $gt: seasonNumber },
                },
            ],
        },
        genericSort: JSON.stringify({
            [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.SeasonNumber`]: 1,
            [`metadata.${RECORDING_MAIN_METADATA_LANGUAGE}.episodeNumber`]: 1,
        }),
        limit: 1,
    };

    return getRecordingsUrl(queryParams);
};

export const getNextVodEpisodeUrl = (
    seriesRef: string,
    seasonNumber: number,
    episodeNumber: number,
) => {
    const query = {
        filter: JSON.stringify({
            'editorial.seriesRef': { $in: [seriesRef] },
            isValid: true,
            isVisible: true,
            $or: [
                {
                    'editorial.SeasonNumber': seasonNumber,
                    'editorial.episodeNumber': { $gt: episodeNumber },
                },
                { 'editorial.SeasonNumber': { $gt: seasonNumber } },
            ],
        }),
        limit: 1,
        sort: JSON.stringify([
            ['editorial.SeasonNumber', 1],
            ['editorial.episodeNumber', 1],
        ]),
    };

    return getVodDetailsUrl(query);
};
